import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';

interface IApiEnvelope<T> {
  data: T;
  message?: string;
  meta?: unknown;
}

const BASE = '/api/v1/contacil/contas-bancarias';

// Natureza da conta (tb_contacil_banco_conta.tipo). 'aplicacao' vem do cadastro
// manual; corrente/poupanca/investimento/salario sao detectados pelo ETL.
export type TipoContaBancaria = 'corrente' | 'aplicacao' | 'poupanca' | 'investimento' | 'salario';

export interface IContaBancaria {
  id: number | null; // null = detectada nos extratos, ainda não cadastrada
  banco_nome: string;
  banco_id_febraban?: string | null;
  agencia?: string | null;
  conta: string;
  tipo: TipoContaBancaria;
  poc_codigo?: string | null;
  poc_nome?: string | null;
  registrada: boolean;
  qtd_lancamentos: number;
  pendentes: number;
}

export interface IContaResult {
  conta: IContaBancaria;
  conciliados: number;
  // perna-banco preenchida mas ainda pendente (falta a contrapartida).
  aguardando_contrapartida?: number;
}

// `banco` é opcional e recorta as contas daquele banco. Quem resolve o
// vocabulário é o SERVIDOR: pode chegar o slug, o rótulo de exibição ou o nome
// gravado pelo ETL — os três encontram as mesmas contas. Fazer esse casamento
// aqui reintroduziria o defeito que o filtro de banco existe para fechar.
export const useContaCilContas = (empresaId?: number | null, banco?: string | null) =>
  useQuery({
    queryKey: ['contacil', 'contas', empresaId ?? null, banco ?? ''] as const,
    enabled: empresaId != null,
    queryFn: async (): Promise<IContaBancaria[]> => {
      const params: Record<string, unknown> = { empresa_id: empresaId };
      if (banco) params.banco = banco;
      const res = await api.get<IApiEnvelope<IContaBancaria[]>>(BASE, { params });
      return res.data.data;
    },
  });

export interface IPlanoBanco {
  poc_codigo: string;
  poc_nome: string;
}

// Contas-banco (classificação 11102) do plano de contas da empresa — pro picker
// do extrato quando a empresa ainda não tem conta cadastrada (toolbar vazia).
export const usePlanoBancos = (empresaId?: number | null) =>
  useQuery({
    queryKey: ['contacil', 'plano-bancos', empresaId ?? null] as const,
    enabled: empresaId != null,
    queryFn: async (): Promise<IPlanoBanco[]> => {
      const res = await api.get<IApiEnvelope<IPlanoBanco[]>>(`${BASE}/plano-bancos`, {
        params: { empresa_id: empresaId },
      });
      return res.data.data;
    },
  });

const useInvalidarContas = () => {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ['contacil', 'contas'] });
    qc.invalidateQueries({ queryKey: ['contacil', 'lancamentos'] });
    // KPIs/quebras do Conciliar — sem isto, o resumo fica defasado após o backfill.
    qc.invalidateQueries({ queryKey: ['contacil', 'lancamentos-resumo'] });
  };
};

export interface IContaCriarPayload {
  empresa_id: number;
  banco_nome: string;
  banco_id_febraban?: string | null;
  agencia?: string | null;
  conta: string;
  tipo?: TipoContaBancaria;
  poc_codigo?: string | null;
}

export const useCriarConta = () => {
  const invalidar = useInvalidarContas();
  return useMutation({
    mutationFn: async (payload: IContaCriarPayload): Promise<IContaResult> => {
      const res = await api.post<IApiEnvelope<IContaResult>>(BASE, payload);
      return res.data.data;
    },
    onSuccess: invalidar,
  });
};

export const useEditarConta = () => {
  const invalidar = useInvalidarContas();
  return useMutation({
    mutationFn: async (params: {
      id: number;
      agencia?: string | null;
      tipo?: TipoContaBancaria;
      poc_codigo?: string | null;
    }): Promise<IContaResult> => {
      const { id, ...body } = params;
      const res = await api.put<IApiEnvelope<IContaResult>>(`${BASE}/${id}`, body);
      return res.data.data;
    },
    onSuccess: invalidar,
  });
};

export const useExcluirConta = () => {
  const invalidar = useInvalidarContas();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await api.delete(`${BASE}/${id}`);
    },
    onSuccess: invalidar,
  });
};

// Reidentifica o banco de uma conta ("Não identificado" -> banco real). Os
// lançamentos migram e, se já existir conta com o mesmo banco+conta, as duas
// são mescladas no backend. Resolve o split "bradesco" + "Não identificado".
export const useReidentificarConta = () => {
  const invalidar = useInvalidarContas();
  return useMutation({
    mutationFn: async (params: {
      id: number;
      banco_nome: string;
      banco_id_febraban?: string | null;
    }): Promise<IContaResult> => {
      const { id, ...body } = params;
      const res = await api.post<IApiEnvelope<IContaResult>>(`${BASE}/${id}/reidentificar`, body);
      return res.data.data;
    },
    onSuccess: invalidar,
  });
};
